import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Instagram, Linkedin, Youtube, ArrowUpRight } from 'lucide-react';

// TikTok and Facebook icons as SVG components
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const FacebookIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

export default function Footer({ settings = {} }) {
  const currentYear = new Date().getFullYear();

  const socialLinks = [];
  if (settings.social_instagram) {
    socialLinks.push({ name: 'Instagram', icon: Instagram, url: settings.social_instagram });
  }
  if (settings.social_linkedin) {
    socialLinks.push({ name: 'LinkedIn', icon: Linkedin, url: settings.social_linkedin });
  }
  if (settings.social_youtube) {
    socialLinks.push({ name: 'YouTube', icon: Youtube, url: settings.social_youtube });
  }
  if (settings.social_tiktok) {
    socialLinks.push({ name: 'TikTok', icon: TikTokIcon, url: settings.social_tiktok });
  }
  if (settings.social_facebook) {
    socialLinks.push({ name: 'Facebook', icon: FacebookIcon, url: settings.social_facebook });
  }

  const footerLinks = [
    { name: 'Impressum', path: '/imprint' },
    { name: 'Datenschutz', path: '/privacy' },
    { name: 'AGB', path: '/terms' },
    { name: 'FAQ', path: '/faq' },
  ];

  return (
    <footer className="bg-ink text-white border-t-4 border-yellow">
      <div className="container-custom py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-8">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-flex items-center gap-3 mb-6">
              <img
                src="/logo-mark-yellow.png"
                alt="Someones Direction"
                className="h-10 w-auto"
              />
              <span className="font-body text-sm tracking-[0.35em] uppercase text-white font-semibold">
                Someones&nbsp;Direction
              </span>
            </Link>
            <p className="text-white/60 text-sm leading-relaxed max-w-md mb-3">
              Foto, Video, Analyse und Social Media als ganzheitliche Produktion,
              strategisch gedacht und präzise umgesetzt.
            </p>
            <p className="font-accent text-yellow text-2xl mb-6">Clear direction. Strong work.</p>
            {socialLinks.length > 0 && (
              <div className="flex items-center space-x-4">
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-11 h-11 rounded-full border border-white/25 flex items-center justify-center text-white/70 hover:text-ink hover:bg-yellow hover:border-yellow transition-colors"
                    aria-label={social.name}
                  >
                    <social.icon className="w-4 h-4" />
                  </motion.a>
                ))}
              </div>
            )}
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display text-sm tracking-wider text-yellow mb-6">
              Kontakt
            </h4>
            <div className="space-y-4 text-sm text-white/70">
              <p>
                <a href={`mailto:${settings.contact_email || 'office@someones-direction.at'}`} className="hover:text-white transition-colors">
                  {settings.contact_email || 'office@someones-direction.at'}
                </a>
              </p>
              <p>
                <a href="tel:+43681804564" className="hover:text-white transition-colors">+43 681 804564</a>
              </p>
              <p>
                Someones Direction<br />
                Leopoldauer Straße 207<br />
                1210 Wien, Österreich
              </p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-sm tracking-wider text-yellow mb-6">
              Links
            </h4>
            <div className="space-y-3">
              {[
                { name: 'Portfolio', to: '/portfolio' },
                { name: 'Leistungen', to: '/services' },
                { name: 'About us', to: '/#about' },
                { name: 'Kontakt', to: '/#contact' },
              ].map((link) => (
                <Link
                  key={link.name}
                  to={link.to}
                  className="flex items-center text-sm text-white/70 hover:text-white transition-colors group"
                >
                  {link.name}
                  <ArrowUpRight className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              ))}
              <a
                href="https://someones-studio.fly.dev/anmelden"
                className="flex items-center text-sm text-white/70 hover:text-white transition-colors group"
              >
                Kundenportal
                <ArrowUpRight className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/15 flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
          <p className="text-xs text-white/50">
            © {currentYear} SOMEONES DIRECTION · Sherif Mobarak · Wien. Alle Rechte vorbehalten.
          </p>
          <div className="flex items-center space-x-6">
            {footerLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className="text-xs text-white/50 hover:text-yellow transition-colors"
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
